<script lang="ts">
  /**
   * DashboardLayout — wrapper com tabs (Visão Geral / Relatório / Modo Call)
   * e ClearSessionButton no topo.
   *
   * Despacha evento 'clearSession' quando o usuário confirma a limpeza.
   * Exibe MetricDetailPage quando um drill-down está ativo no store.
   */
  import { createEventDispatcher } from 'svelte';
  import type { DiagnosticResult } from '$core/diagnostic.types';
  import DashboardPage from './DashboardPage.svelte';
  import MetricDetailPage from './MetricDetailPage.svelte';
  import PresentationMode from './PresentationMode.svelte';
  import ReportPage from '../report/ReportPage.svelte';
  import ClearSessionButton from '../session/ClearSessionButton.svelte';
  import { appStore } from '../../app/store';

  export let result: DiagnosticResult;

  type Tab = 'overview' | 'report' | 'presentation';

  let activeTab: Tab = 'overview';

  const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: 'Visão Geral' },
    { id: 'report', label: 'Relatório' },
    { id: 'presentation', label: 'Modo Call' },
  ];

  const dispatch = createEventDispatcher<{ clearSession: void }>();

  function handleCleared() {
    dispatch('clearSession');
  }

  function setTab(tab: Tab) {
    activeTab = tab;
    // Fechar drill-down ao trocar de aba
    appStore.closeDrillDown();
  }

  // Reativamente observar o estado de drill-down
  $: drillDownState = $appState.drillDown;
  const appState = appStore.state;
</script>

<div class="dashboard-layout">
  <div class="dashboard-layout__topbar">
    <nav class="dashboard-layout__tabs" aria-label="Abas do dashboard">
      {#each TABS as tab (tab.id)}
        <button
          class="dashboard-layout__tab"
          class:dashboard-layout__tab--active={activeTab === tab.id}
          type="button"
          aria-current={activeTab === tab.id ? 'page' : undefined}
          on:click={() => setTab(tab.id)}
        >
          {tab.label}
        </button>
      {/each}
    </nav>

    <div class="dashboard-layout__actions">
      <ClearSessionButton store={appStore} on:cleared={handleCleared} />
    </div>
  </div>

  <div class="dashboard-layout__content">
    {#if drillDownState !== null}
      <MetricDetailPage
        {result}
        metricId={drillDownState.metricId}
        assumptions={$appState.assumptions}
        focusedEmployeeId={drillDownState.focusedEmployeeId}
        onBack={() => appStore.closeDrillDown()}
        onFocusEmployee={(id) => appStore.focusEmployeeInDrillDown(id)}
        onUnfocusEmployee={() => appStore.unfocusEmployee()}
      />
    {:else if activeTab === 'overview'}
      <DashboardPage {result} />
    {:else if activeTab === 'report'}
      <ReportPage {result} />
    {:else if activeTab === 'presentation'}
      <PresentationMode {result} on:exit={() => setTab('overview')} />
    {/if}
  </div>
</div>

<style>
  .dashboard-layout {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .dashboard-layout__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 2px solid var(--ptt-gray-20, #e7e7e7);
    padding-bottom: 0;
    gap: var(--ptt-space-4, 16px);
    flex-wrap: wrap;
  }

  .dashboard-layout__tabs {
    display: flex;
    gap: 0;
  }

  .dashboard-layout__tab {
    padding: var(--ptt-space-3, 12px) var(--ptt-space-5, 24px);
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--ptt-gray-200, #565656);
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;
    white-space: nowrap;
    margin-bottom: -2px;
  }

  .dashboard-layout__tab:hover {
    color: var(--ptt-gray-300, #323232);
  }

  .dashboard-layout__tab--active {
    color: var(--ptt-purple-100, #5B2FD4);
    border-bottom-color: var(--ptt-purple-100, #5B2FD4);
  }

  .dashboard-layout__actions {
    display: flex;
    align-items: center;
    padding-bottom: var(--ptt-space-2, 8px);
  }

  .dashboard-layout__content {
    padding-top: var(--ptt-space-5, 1.25rem);
  }
</style>
